/**
 * Phase 6 / Journey B — Client Portal white-label helpers.
 *
 * Centralises how the portal turns a client's brand config (currently just
 * `shop_brand_color` on the magic-link identity) into the handful of derived
 * values every screen needs, so the dashboard, reports, login and expired
 * pages all read the same brand the same way. Keeping the math here means
 * "reads brand config" is true everywhere — not re-implemented per page.
 */
import type { ClientPortalIdentity } from '../../store/reducers/authSlice';

/** Neutral fallback when a brand hasn't set a colour. Matches the legacy ink. */
export const PORTAL_NEUTRAL = '#101828';

export interface PortalBrand {
  /** Primary brand colour (header band, accents). */
  primary: string;
  /** Readable text colour to sit ON the primary (black/white by luminance). */
  onPrimary: string;
  /** Very light tint of the brand for soft surfaces / icon chips. */
  softBg: string;
  /** Up to two initials for an avatar when no logo is provided. */
  initials: string;
  /** Display name for the shop. */
  shopName: string;
}

const HEX_RE = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

function normaliseHex(input?: string | null): string | null {
  if (!input) return null;
  const m = input.trim().match(HEX_RE);
  if (!m) return null;
  let hex = m[1];
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((c) => c + c)
      .join('');
  }
  return `#${hex.toLowerCase()}`;
}

function toRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

/**
 * WCAG relative luminance → pick black or white text for contrast. Simpler
 * than full contrast-ratio math but reliable for "is this colour dark?".
 */
function readableTextColor(hex: string): string {
  const [r, g, b] = toRgb(hex).map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return lum > 0.5 ? '#101828' : '#ffffff';
}

function softTint(hex: string): string {
  const [r, g, b] = toRgb(hex);
  // 12% brand over white — a gentle surface that always stays light.
  const mix = (c: number) => Math.round(c * 0.12 + 255 * 0.88);
  return `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`;
}

function initialsOf(name?: string | null, fallback?: string | null): string {
  const src = (name || fallback || '').trim();
  if (!src) return '•';
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function getPortalBrand(
  identity?: ClientPortalIdentity | null
): PortalBrand {
  const primary = normaliseHex(identity?.shop_brand_color) || PORTAL_NEUTRAL;
  const shopName = identity?.shop_name || 'Your portal';
  return {
    primary,
    onPrimary: readableTextColor(primary),
    softBg: softTint(primary),
    initials: initialsOf(identity?.shop_name, identity?.name),
    shopName,
  };
}

/**
 * Auto-summary headline for the dashboard hero. Turns "how many proposals are
 * waiting" into one friendly, on-brand sentence the client reads first.
 */
export function buildPortalSummary(pendingCount: number): {
  headline: string;
  sub: string;
} {
  if (pendingCount <= 0) {
    return {
      headline: "You're all set",
      sub: 'Nothing needs your attention right now. Your agency is on it — we’ll email you if anything comes up.',
    };
  }
  const noun = pendingCount === 1 ? 'change' : 'changes';
  return {
    headline:
      pendingCount === 1
        ? '1 change is waiting for your sign-off'
        : `${pendingCount} changes are waiting for your sign-off`,
    sub: `Your agency has proposed ${pendingCount} ${noun} for your accounts. Review them below when you have a moment.`,
  };
}
